/**
 * Round payload validation and merging.
 *
 * A decrypted payload is untrusted input like anything else off the wire, so it
 * is structurally checked before it reaches the store. Nothing here may read an
 * answer-bearing field, because the server never puts one in (invariant 5).
 *
 * Deeper rungs arrive after the player pushes — see `mergeRungs`.
 *
 * Every rung is now a DISCRIMINATED UNION on `kind` (change 5, change 6): a
 * true/false rung has two options, a multiple choice has four, an ordering has
 * rows, and the golden round's open numeric rung has none at all. Validation
 * therefore branches on the discriminant rather than probing for the presence
 * of fields, which is what makes an unknown or mismatched shape a clean
 * rejection instead of a half-built question.
 */

import type {
  AnswerKind,
  LngLat,
  NumericAnswerSpec,
  PinpointAttemptPolicy,
  RoundPayload,
  RoundPayloadOption,
  RoundPayloadRung,
} from '@geogame/shared';
import { NUMERIC_SPECS, isAnswerKind, isNumericQuantity, isProbeType } from '@geogame/shared';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isOption(v: unknown): v is RoundPayloadOption {
  if (!isRecord(v)) return false;
  // Two fields, and there is no third. `imageUrl` used to live here and it was
  // the flag rung's answer key spelled out in a URL; the artwork now arrives as
  // sealed bytes behind the rung's own `assetIdx`.
  return typeof v['id'] === 'string' && typeof v['label'] === 'string';
}

/** A non-negative integer index into the arm's `assetTokens`, or absent. */
function isAssetIdx(v: unknown): boolean {
  return v === undefined || (Number.isInteger(v) && (v as number) >= 0);
}

function optionsOf(v: unknown): readonly RoundPayloadOption[] | null {
  if (!Array.isArray(v) || !v.every(isOption)) return null;
  return v as readonly RoundPayloadOption[];
}

/**
 * A numeric spec is only accepted when it MATCHES the canonical entry for its
 * quantity.
 *
 * Not merely "is well shaped": the bounds and the tolerance are constants of
 * the quantity, and a server (or a proxy) that narrowed them around the answer
 * would be handing this client an oracle. Comparing against `NUMERIC_SPECS`
 * makes that unrepresentable rather than merely unlikely.
 */
function numericSpecOf(v: unknown): NumericAnswerSpec | null {
  if (!isRecord(v)) return null;
  const quantity = v['quantity'];
  if (!isNumericQuantity(quantity)) return null;
  const canonical = NUMERIC_SPECS[quantity];
  if (v['min'] !== canonical.min || v['max'] !== canonical.max) return null;
  if (v['step'] !== canonical.step) return null;
  return canonical;
}

function goldenOf(v: Record<string, unknown>): { golden?: true } {
  return v['golden'] === true ? { golden: true } : {};
}

/**
 * Build one rung from untrusted input, or null.
 *
 * Shared by `parseRoundPayload` (the outer, still-sealed stubs) and
 * `parseSealedRung` (an inner blob the player has just unlocked), because both
 * are the same union and both are equally untrusted.
 */
/**
 * The dots of a map choice, or `undefined` when there are none.
 *
 * `null` means MALFORMED and `undefined` means ABSENT, which is the same
 * distinction `optionsOf` draws — an ordinary four-option probe has no markers
 * and must not be rejected for it.
 */
function markersOf(v: unknown): readonly LngLat[] | null | undefined {
  if (v === undefined) return undefined;
  if (!Array.isArray(v) || v.length === 0) return null;
  const out: LngLat[] = [];
  for (const entry of v) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const [lng, lat] = entry as unknown[];
    if (typeof lng !== 'number' || typeof lat !== 'number') return null;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
    out.push([lng, lat]);
  }
  return out;
}

/** The candidate names of a map choice. Same null/undefined split as above. */
function namesOf(v: unknown): readonly string[] | null | undefined {
  if (v === undefined) return undefined;
  if (!Array.isArray(v) || v.length === 0) return null;
  if (!v.every((n) => typeof n === 'string' && n.length > 0)) return null;
  return v as readonly string[];
}

function rungOf(v: unknown): RoundPayloadRung | null {
  if (!isRecord(v)) return null;
  if (!Number.isInteger(v['rung']) || typeof v['prompt'] !== 'string') return null;
  const rung = v['rung'] as number;
  const prompt = v['prompt'];
  const golden = goldenOf(v);

  switch (v['kind']) {
    case 'pinpoint':
      if (v['probeType'] !== 'pinpoint') return null;
      return { kind: 'pinpoint', probeType: 'pinpoint', rung, prompt, ...golden };

    case 'sealed': {
      if (!isProbeType(v['probeType']) || !isAnswerKind(v['answerKind'])) return null;
      return {
        kind: 'sealed',
        probeType: v['probeType'],
        answerKind: v['answerKind'] as AnswerKind,
        rung,
        prompt,
        ...golden,
      };
    }

    case 'choice': {
      if (!isProbeType(v['probeType'])) return null;
      const options = optionsOf(v['options']);
      if (options === null || !isAssetIdx(v['assetIdx'])) return null;
      // A MAP CHOICE. Both arrays or neither, and one entry of each per option:
      // a rung with three dots and four options would render a globe the player
      // cannot fully answer on, and the store would rather show them the stall
      // card than a question with a hole in it.
      const markers = markersOf(v['markers']);
      const names = namesOf(v['names']);
      if (markers === null || names === null) return null;
      if (markers !== undefined || names !== undefined) {
        if (markers?.length !== options.length || names?.length !== options.length) return null;
        return {
          kind: 'choice',
          probeType: v['probeType'],
          rung,
          prompt,
          options,
          markers,
          names,
          ...golden,
        };
      }
      return {
        kind: 'choice',
        probeType: v['probeType'],
        rung,
        prompt,
        options,
        ...(v['assetIdx'] !== undefined ? { assetIdx: v['assetIdx'] as number } : {}),
        ...golden,
      };
    }

    case 'boolean': {
      if (!isProbeType(v['probeType'])) return null;
      const options = optionsOf(v['options']);
      // Exactly two. A true/false rung with three options is not a true/false
      // rung, and the tuple type says so.
      if (options === null || options.length !== 2) return null;
      const pair = options as readonly [RoundPayloadOption, RoundPayloadOption];
      return { kind: 'boolean', probeType: v['probeType'], rung, prompt, options: pair, ...golden };
    }

    case 'ordering': {
      if (!isProbeType(v['probeType'])) return null;
      const options = optionsOf(v['options']);
      if (options === null || options.length < 2) return null;
      return { kind: 'ordering', probeType: v['probeType'], rung, prompt, options, ...golden };
    }

    case 'numeric': {
      if (!isProbeType(v['probeType'])) return null;
      const numeric = numericSpecOf(v['numeric']);
      if (numeric === null) return null;
      // An open numeric rung has NO options. One arriving with them is either a
      // bug or an attempt to turn a 0%-floor question into a 25%-floor one.
      if (v['options'] !== undefined && v['options'] !== null) return null;
      return { kind: 'numeric', probeType: v['probeType'], rung, prompt, numeric, ...golden };
    }

    default:
      return null;
  }
}

function isAttemptPolicy(v: unknown): v is PinpointAttemptPolicy {
  if (!isRecord(v)) return false;
  const mult = v['multipliers'];
  if (!Array.isArray(mult) || mult.length === 0) return false;
  if (!mult.every((m) => typeof m === 'number' && Number.isFinite(m) && m > 0 && m <= 1)) {
    return false;
  }
  return v['maxAttempts'] === mult.length;
}

export function parseRoundPayload(v: unknown): RoundPayload | null {
  if (!isRecord(v)) return null;
  if (!Number.isInteger(v['roundIdx'])) return null;
  if (
    v['gameMode'] !== undefined &&
    v['gameMode'] !== 'country' &&
    v['gameMode'] !== 'city' &&
    v['gameMode'] !== 'state'
  ) {
    return null;
  }
  if (typeof v['prompt'] !== 'string') return null;
  if (!Number.isInteger(v['ladderDepth'])) return null;
  if (typeof v['secondsPerRung'] !== 'number' || typeof v['decisionSeconds'] !== 'number') {
    return null;
  }
  const rungs = v['rungs'];
  if (!Array.isArray(rungs)) return null;
  if (!rungs.every((r) => rungOf(r) !== null)) return null;
  const orientRegion = v['orientRegion'];
  if (orientRegion !== undefined && typeof orientRegion !== 'string') return null;
  // CHANGE 4. The retry price has to be here, and has to be well formed: the
  // player is shown it before they commit, so a malformed one would advertise a
  // cost the server does not charge.
  if (!isAttemptPolicy(v['pinpointAttempts'])) return null;
  if (v['golden'] !== undefined && typeof v['golden'] !== 'boolean') return null;
  return v as unknown as RoundPayload;
}

/**
 * Fold a newly-delivered payload's rungs into the ones already known for the
 * round, keyed by rung number. Later deliveries win.
 *
 * A rung-2 prompt ("capital of ...") names the country, so it cannot be shipped
 * before rung 1 is locked. The gateway therefore re-arms the same `roundIdx`
 * with the deeper rung once the player pushes, and this is where those land.
 */
export function mergeRungs(
  existing: Readonly<Record<number, RoundPayloadRung>>,
  incoming: readonly RoundPayloadRung[],
): Record<number, RoundPayloadRung> {
  const out: Record<number, RoundPayloadRung> = { ...existing };
  for (const r of incoming) out[r.rung] = r;
  return out;
}

/**
 * The plaintext inside an inline sealed rung — see server `room.ts` `armRound`,
 * which seals exactly this shape per rung 2..N.
 *
 * Structurally validated like everything else off the wire, and for the same
 * reason: a decrypted blob is still untrusted input.
 */
export function parseSealedRung(v: unknown): RoundPayloadRung | null {
  return rungOf(v);
}
